import { acceptHMRUpdate, defineStore } from 'pinia'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import { getCustomerList } from '../scripts/customers'
import { useCustomers } from '@/stores/customers'
import { getVehicles } from '../scripts/task'
import { getOrgImplements } from '../scripts/task'
import { isValidObject } from '../helpers/objectHelper'
import i18n from '@/i18n'

export const useVehicleManagementStore = defineStore('vehicleManagement', {
    state: () => ({
        vehicles: [],
        selectedVehicle: null,
        filters: null,
        searchQuery: '',
        loading: false,
        organizations: [],
        implementsModels: [],
        otherWords: [
            i18n.global.t('No vehicles'),
            i18n.global.t('No implements'),
            'N/A'
        ],
        selectedImplement: null,
        implementOrg: null,
        implementDialogVisible: false
    }),
    actions: {
        clearFilter() {
            this.initFilters()
            this.searchQuery = ''
        },
        initFilters() {
            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: {
                    operator: FilterOperator.AND,
                    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
                }
            }
        },
        filteredVehicles() {
            if (!this.searchQuery) {
                return this.organizations
            }
            return this.organizations.filter(organization => 
                organization.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                (organization.vehicles && organization.vehicles.some(vehicle => 
                    vehicle.vehicle_serial.toString().includes(this.searchQuery) ||
                    this.getVehicleOrganizationName(vehicle).toLowerCase().includes(this.searchQuery.toLowerCase())
                )) ||
                // Search in implements by type
                (organization.implements && organization.implements.some(category => 
                    category.implements && category.implements.some(implement => 
                        implement.implement_serial.toString().includes(this.searchQuery) ||
                        implement.implement_name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                        this.getImplementType(implement).toLowerCase().includes(this.searchQuery.toLowerCase())
                    )
                )) ||
                // Search for otherWords - for organizations without vehicles
                ((!organization.vehicles || organization.vehicles.length === 0) && this.otherWords.some(word => 
                    word.toLowerCase().includes(this.searchQuery.toLowerCase())
                )) ||
                ((!organization.implements || organization.implements.length === 0) && this.otherWords.some(word => 
                    word.toLowerCase().includes(this.searchQuery.toLowerCase())
                ))
            )
        },
        async getOrganizations() {

            try {
                const customers = await getCustomerList()
                if (customers && customers.length > 0) {
                    this.organizations = customers
                } else {
                    this.organizations = []
                }
            } catch (error) {
                console.error('Error getting organizations:', error)
                return false
            }
            return true
        },
        getOrganization(orgId) {
            const cs = useCustomers()
            const organization = cs.customers.find((org) => org.organization_id === orgId || org.id === orgId)
            return organization
        },
        getVehicleOrganizationName(vehicle) {
            const organization = this.organizations.find(org => org.id === vehicle.organization_id)
            return organization ? organization.name : null
        },
        async loadVehicles() {

            try {
                const vehicles = await getVehicles()
                if (isValidObject(vehicles)) {
                    this.vehicles = vehicles

                    // Add vehicles to organizations
                    this.organizations.forEach(organization => {
                        organization.vehicles = []
                        const orgVehicles = this.vehicles[organization.id]
                        if (orgVehicles && orgVehicles.length > 0) {
                            const vehiclesWithOrg = orgVehicles.map(vehicle => ({
                                ...vehicle,
                                organization_id: organization.id
                            }))
                            organization.vehicles.push(...vehiclesWithOrg)
                        }
                    })
                    return true
                } else {
                    this.vehicles = []
                }
            } catch (error) {
                console.error('Error getting vehicles:', error)
                return false
            }
        },
        async getImplementsModels() {

            try {
                const implementsModels = await getOrgImplements()
                if (isValidObject(implementsModels)) {
                    this.implementsModels = implementsModels

                    // Add implements to organizations
                    this.organizations.forEach(organization => {
                        organization.implements = []
                        const orgImplements = this.implementsModels[organization.id]
                        if (orgImplements && orgImplements.length > 0) {
                            const implementsWithOrg = orgImplements.map(implement => ({
                                ...implement,
                                organization_id: organization.id
                            }))
                            organization.implements.push(...implementsWithOrg)
                        }
                    })

                    this.sortImplements()

                    return true
                } else {
                    this.implementsModels = []
                }
            } catch (error) {
                console.error('Error getting implements models:', error)
                return false
            }
        },
        getVehicleYear(vehicle) {
            const length = vehicle.vehicle_serial.toString().length

            switch (length) {
                case 7:
                    return vehicle.vehicle_serial.toString().slice(0, 2)
                case 1:
                    return vehicle.vehicle_serial.toString().slice(0, 1)
                default:
                    return null
            }
        },
        sortImplements() {
            // Categories for implements
            const categories = {
                seehhopper: ['SH', 'SD', 'PL', 'SE', 'LR', 'RH', 'RC', 'CR', 'DS', 'TC'],
                other: [], // All other that are not in seehhopper or nexco
                nexco: ['CO', 'DR', 'SF', 'TR'] // + NXC (3 letters) - verified and described below
            }

            this.organizations.forEach(organization => {
                if (organization.implements && organization.implements.length > 0) {
                    // Save original implements
                    const originalImplements = [...organization.implements]

                    // Initialize array for categories
                    organization.implements = [
                        {
                            model: 'seehhopper',
                            implements: []
                        },
                        {
                            model: 'other',
                            implements: []
                        },
                        {
                            model: 'nexco',
                            implements: []
                        }
                    ]

                    // Sort implements in the correct category
                    originalImplements.forEach(implement => {
                        const implementName = implement.implement_name

                        if (implementName && implementName.length >= 4) {
                            // get only letters
                            const lettersOnly = implementName.replace(/[^A-Za-z]/g, '')

                            if (lettersOnly.length >= 4) {
                                // get last two letters
                                const lastTwoLetters = lettersOnly.substring(lettersOnly.length - 2)

                                if (categories.seehhopper.includes(lastTwoLetters)) {
                                    organization.implements[0].implements.push(implement)
                                } else if (categories.nexco.includes(lastTwoLetters)) {
                                    organization.implements[2].implements.push(implement)
                                } else {
                                    organization.implements[1].implements.push(implement)
                                }
                            } else if (lettersOnly.length === 3 && lettersOnly === 'NXC') {
                                // special case for NXC (3 letters)
                                organization.implements[2].implements.push(implement)
                            } else {
                                // if it doesn't have 4 letters or it's not NXC, put it in "other"
                                organization.implements[1].implements.push(implement)
                            }
                        } else {
                            // if implement_name doesn't have the correct format, put it in "other"
                            organization.implements[1].implements.push(implement)
                        }
                    })
                }
            })
        },
        getImplementType(implement) {
            const type = implement.implement_name.substring(2, 4)
            return type
        },
        editImplement(implement, organization) {
            this.selectedImplement = implement
            this.implementOrg = organization
            this.implementDialogVisible = true
        },
        cancelUpdateImplement() {
            this.selectedImplement = null
            this.implementOrg = null
            this.implementDialogVisible = false
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useVehicleManagementStore, import.meta.hot))
}